// AreaMapTable.tsx
import React, {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  MutableRefObject,
  Key,
} from 'react';
import { Table, Form, Paging, usePaging, Select, TreeSelect, Button, Modal, Input, Radio } from '@maxtropy/components';
import { TreeSelectProps, Col, Row, type FormInstance } from 'antd';
import styles from '../index.module.scss';
import {
  apiV2DistributionCabinetRoomListPost,
  apiV2EnergyAnalysisGroupAndUnitPost,
  apiV2AreaTreePost,
  V2ElectricMapConfigOverviewDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import DeviceModal, { DeviceModalRef } from './DeviceModal';
import { isNil, set } from 'lodash-es';
import DeviceGgdModal from './DeviceGgdModal';
import DeviceSunLightModal from './DeviceSunLightModal';
import BindEnergyUnitModal from './BindEnergyUnitModal';

interface Option {
  label: number | undefined | string;
  value: number | undefined | string;
}
type MapDetailProp = NonNullable<V2ElectricMapConfigOverviewDetailPostResponse>;
export type AreaMapProp = Exclude<MapDetailProp['areaMapList'], undefined>[number];

interface AreaMapTableProps {
  dataSource?: AreaMapProp[];
  fullDataSource?: AreaMapProp[];
  isEditing: boolean;
  form?: FormInstance;
  onLoadingChange?: (loading: boolean) => void;
}

export interface AreaMapTableRef {
  getAllData: () => AreaMapProp[];
}

const AreaMapTable = forwardRef<AreaMapTableRef, AreaMapTableProps>(
  ({ dataSource, fullDataSource, isEditing, form, onLoadingChange }, ref) => {
    const pagingInfo = usePaging(10);
    const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
    const deviceRef: MutableRefObject<DeviceModalRef | null> = useRef(null);
    const deviceGgdRef: MutableRefObject<DeviceModalRef | null> = useRef(null);
    const deviceSunLightRef: MutableRefObject<DeviceModalRef | null> = useRef(null);
    const [standHomeOp, setStandHome] = useState<Option[] | undefined>([]);
    const [treeNodeData, setTreeNodeData] = useState<TreeSelectProps['treeData']>([]);
    const [areaTreeData, setAreaTreeData] = useState<TreeSelectProps['treeData']>([]);
    const [nodeMap, setNodeMap] = useState<
      Record<string | number, { energyUnitId?: number; energyUnitGroupId?: number }>
    >({});
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [bindDeviceIds, setBindDeviceIds] = useState<Key[]>();
    const [currentIndex, setCurrentIndex] = useState<number>();
    // 高杆灯
    const [ggdOpenModal, setGgdOpenModal] = useState<boolean>(false);
    const [bindGgdDeviceIds, setBindGgdDeviceIds] = useState<Key[]>();
    // 太阳能路灯
    const [sunLightOpenModal, setSunLightOpenModal] = useState<boolean>(false);
    const [bindSunLightDeviceIds, setBindSunLightDeviceIds] = useState<Key[]>();
    // 关联热区
    const [hotZoneOpenModal, setHotZoneOpenModal] = useState<boolean>(false);
    const [hotZoneForm] = Form.useForm();
    // 绑定用能单元（多选）
    const [bindEnergyUnitModalOpen, setBindEnergyUnitModalOpen] = useState<boolean>(false);

    useEffect(() => {
      onLoadingChange?.(true);
      Promise.all([
        // 站房
        apiV2DistributionCabinetRoomListPost({}).then(res => {
          const dataRes = res.list ?? [];
          const options = dataRes.map(i => ({
            label: i.name,
            value: i.id,
          }));
          setStandHome(options);
        }),
        // tree
        apiV2EnergyAnalysisGroupAndUnitPost({}).then(res => {
          const map: any = {};
          const tree = formatToNodeData(res.list || [], map);
          setTreeNodeData(tree);
          setNodeMap(map);
        }),
        apiV2AreaTreePost({}).then(res => {
          setAreaTreeData(formatAreaTreeData(res.list ?? []));
        }),
      ])
        .catch(() => undefined)
        .finally(() => onLoadingChange?.(false));
    }, []);

    useEffect(() => {
      setPageOffset(1);
      setTotalCount((dataSource ?? []).length);
    }, [dataSource]);

    // 使用 ref 暴露方法，返回合并了表单值的完整数据
    useImperativeHandle(
      ref,
      () => ({
        getAllData: () => {
          if (!form) return fullDataSource ?? [];
          const resdata = (fullDataSource ?? []).map((item, index) => {
            const energyUnitId = form.getFieldValue(['areaMapList', index, 'energyUnitId']);
            const energyUnitGroupId = nodeMap[energyUnitId!]?.energyUnitGroupId;

            return {
              ...item,
              energyUnitId,
              energyUnitGroupId,
              roomId: form.getFieldValue(['areaMapList', index, 'roomId']),
              zoneAlias: form.getFieldValue(['areaMapList', index, 'zoneAlias']),
              deviceIds: form.getFieldValue(['areaMapList', index, 'deviceIds']),
              ggdDeviceId: form.getFieldValue(['areaMapList', index, 'ggdDeviceId']),
              circuitId: form.getFieldValue(['areaMapList', index, 'circuitId']),
              url: form.getFieldValue(['areaMapList', index, 'url']),
              newPage: form.getFieldValue(['areaMapList', index, 'newPage']),
              overviewEnergyUnitIds: form.getFieldValue(['areaMapList', index, 'overviewEnergyUnitIds']),
              overviewEnergyUnitGroupId: form.getFieldValue(['areaMapList', index, 'overviewEnergyUnitGroupId']),
              type: form.getFieldValue(['areaMapList', index, 'type']) ?? 2,
              areaIds: (form.getFieldValue(['areaMapList', index, 'areaIds']) ?? [])
                .map((item: number | { value?: number }) => (typeof item === 'object' ? item.value : item))
                .filter((id: number | undefined): id is number => id !== undefined),
              // @ts-ignore 当前 @maxtropy/device-customer-apis-v2 类型未包含 pointLng，接口已支持
              pointLng: form.getFieldValue(['areaMapList', index, 'pointLng']),
              // @ts-ignore 当前 @maxtropy/device-customer-apis-v2 类型未包含 pointLat，接口已支持
              pointLat: form.getFieldValue(['areaMapList', index, 'pointLat']),
              displayWindow: form.getFieldValue(['areaMapList', index, 'displayWindow']) ?? false,
            };
          });
          return resdata;
        },
      }),
      [fullDataSource, form]
    );

    function formatToNodeData(data: any[], map: any = {}, level: number = 0): TreeSelectProps['treeData'] {
      return data.map(i => {
        const children = i.children ?? i.energyUnits;
        const hasChildren = children && children.length > 0;
        const nodeValue = i.energyUnitId ?? i.id;
        // 存到 map
        map[nodeValue] = {
          energyUnitId: i.energyUnitId,
          energyUnitGroupId: i.energyUnitGroupId,
        };

        return {
          title: i.name,
          value: nodeValue,
          energyUnitId: i.energyUnitId,
          energyUnitGroupId: i.energyUnitGroupId,
          children: hasChildren ? formatToNodeData(children, map, level + 1) : [],
          selectable: level !== 0,
        };
      });
    }

    function formatAreaTreeData(data: any[]): TreeSelectProps['treeData'] {
      return data.map(item => ({
        title: item.zoneName,
        value: item.id,
        label: item.zoneName,
        children: item.children ? formatAreaTreeData(item.children) : [],
      }));
    }
    // 当前页数据
    const pagedData = useMemo(() => {
      const start = (pageOffset - 1) * pageSize;
      setTotalCount((dataSource ?? []).length);
      return (dataSource ?? []).slice(start, start + pageSize);
    }, [dataSource, pageOffset, pageSize]);

    const handleOK = async () => {
      const selectedRowKeys: Key[] = deviceRef?.current?.selectedRowKeys || [];
      const areaMapItem = form!.getFieldValue('areaMapList');
      form?.setFieldsValue({ areaMapList: set(areaMapItem, [currentIndex!, 'deviceIds'], selectedRowKeys) });

      setOpenModal(false);
      setCurrentIndex(undefined);
    };

    // 处理高杆灯绑定设备
    const handleGgdOK = async () => {
      const selectedRowKeys: Key[] = deviceGgdRef?.current?.selectedRowKeys || [];
      const areaMapItem = form!.getFieldValue('areaMapList');
      form?.setFieldsValue({ areaMapList: set(areaMapItem, [currentIndex!, 'ggdDeviceId'], selectedRowKeys?.[0]) });

      setGgdOpenModal(false);
      setCurrentIndex(undefined);
    };
    // 处理太阳能路灯绑定设备
    const handleSunLightOK = async () => {
      const selectedRowKeys: Key[] = deviceSunLightRef?.current?.selectedRowKeys || [];
      const areaMapItem = form!.getFieldValue('areaMapList');
      form?.setFieldsValue({ areaMapList: set(areaMapItem, [currentIndex!, 'circuitId'], selectedRowKeys?.[0]) });

      setSunLightOpenModal(false);
      setCurrentIndex(undefined);
    };

    // 处理关联热区
    const handleHotZoneOK = async () => {
      const values = await hotZoneForm.validateFields();
      const areaMapItem = form!.getFieldValue('areaMapList');
      form?.setFieldsValue({
        areaMapList: set(areaMapItem, [currentIndex!, 'url'], values.url),
      });
      form?.setFieldsValue({
        areaMapList: set(areaMapItem, [currentIndex!, 'newPage'], values.newPage),
      });

      setHotZoneOpenModal(false);
      setCurrentIndex(undefined);
      hotZoneForm.resetFields();
    };

    const areaMapColumns = [
      {
        title: '序号',
        width: 100,
        render: (_: any, __: AreaMapProp, index: number) => (pageOffset - 1) * pageSize + index + 1,
      },
      {
        title: '区域名称',
        dataIndex: 'zoneName',
        width: 200,
      },
      {
        title: '区域点击名称',
        dataIndex: 'zoneAlias',
        width: 200,
        render: (_: any, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'zoneAlias']} style={{ marginBottom: 0 }}>
              <Input disabled={!isEditing} style={{ width: 200 }} placeholder="请输入" />
            </Form.Item>
          );
        },
      },
      {
        title: '区域代码',
        dataIndex: 'zoneCode',
        width: 120,
      },
      {
        title: '区域类型',
        dataIndex: 'type',
        width: 170,
        render: (_: unknown, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'type']} initialValue={2} style={{ marginBottom: 0 }}>
              <Radio.Group disabled={!isEditing}>
                <Radio value={1}>站房</Radio>
                <Radio value={2}>区域</Radio>
              </Radio.Group>
            </Form.Item>
          );
        },
      },
      {
        title: '平台区域管理',
        dataIndex: 'areaIds',
        width: 240,
        render: (_: number[], record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'areaIds']} style={{ marginBottom: 0 }}>
              <TreeSelect
                disabled={!isEditing}
                treeData={areaTreeData}
                style={{ width: 220 }}
                allowClear
                showSearch
                treeNodeFilterProp="title"
                treeCheckable
                treeCheckStrictly
                placeholder="请选择"
              />
            </Form.Item>
          );
        },
      },
      {
        title: '浮窗经度',
        dataIndex: 'pointLng',
        width: 180,
        render: (_: unknown, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'pointLng']} style={{ marginBottom: 0 }}>
              <Input disabled={!isEditing} style={{ width: 170 }} placeholder="请输入" />
            </Form.Item>
          );
        },
      },
      {
        title: '浮窗纬度',
        dataIndex: 'pointLat',
        width: 170,
        render: (_: unknown, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'pointLat']} style={{ marginBottom: 0 }}>
              <Input disabled={!isEditing} style={{ width: 160 }} placeholder="请输入" />
            </Form.Item>
          );
        },
      },
      {
        title: '是否展示区域浮窗',
        dataIndex: 'displayWindow',
        width: 190,
        render: (_: boolean, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item
              name={['areaMapList', rowIndex, 'displayWindow']}
              initialValue={false}
              style={{ marginBottom: 0 }}
            >
              <Radio.Group disabled={!isEditing}>
                <Radio value={true}>是</Radio>
                <Radio value={false}>否</Radio>
              </Radio.Group>
            </Form.Item>
          );
        },
      },
      {
        title: '用能单元',
        dataIndex: 'energyUnitId',
        render: (_: any, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'energyUnitId']} style={{ marginBottom: 0 }}>
              <TreeSelect
                disabled={!isEditing}
                treeData={treeNodeData}
                style={{ width: 200 }}
                allowClear
                showSearch
                treeNodeFilterProp="title"
                treeDefaultExpandAll
                placeholder="请选择"
                className={styles['custom-tree-select']}
              />
            </Form.Item>
          );
        },
      },

      {
        title: '站房',
        dataIndex: 'roomId',
        render: (_: any, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Form.Item name={['areaMapList', rowIndex, 'roomId']} style={{ marginBottom: 0 }}>
              <Select
                disabled={!isEditing}
                style={{ width: 200 }}
                placeholder="请选择"
                allowClear
                showSearch
                optionFilterProp="label"
                options={standHomeOp}
              />
            </Form.Item>
          );
        },
      },
      {
        title: '关联用能单元',
        dataIndex: 'overviewEnergyUnitIds',
        render: (_: any, record: AreaMapProp) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Button
              disabled={!isEditing}
              type="link"
              onClick={() => {
                setCurrentIndex(rowIndex);
                setBindEnergyUnitModalOpen(true);
              }}
            >
              编辑
            </Button>
          );
        },
      },
      {
        title: '关联照明设备',
        dataIndex: 'roomId',
        render: (_: number, record: AreaMapProp, index: number) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Button
              disabled={!isEditing}
              type="link"
              onClick={() => {
                setCurrentIndex(rowIndex);
                setBindDeviceIds(form?.getFieldValue(['areaMapList', rowIndex, 'deviceIds']));
                setOpenModal(true);
              }}
            >
              编辑
            </Button>
          );
        },
      },
      {
        title: '关联太阳能路灯',
        dataIndex: 'roomId',
        render: (_: number, record: AreaMapProp, index: number) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Button
              disabled={!isEditing}
              type="link"
              onClick={() => {
                setCurrentIndex(rowIndex);
                setBindSunLightDeviceIds(form?.getFieldValue(['areaMapList', rowIndex, 'circuitId']));
                setSunLightOpenModal(true);
              }}
            >
              编辑
            </Button>
          );
        },
      },
      {
        title: '关联高杆灯',
        dataIndex: 'roomId',
        render: (_: number, record: AreaMapProp, index: number) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Button
              disabled={!isEditing}
              type="link"
              onClick={() => {
                setCurrentIndex(rowIndex);
                setBindGgdDeviceIds(form?.getFieldValue(['areaMapList', rowIndex, 'ggdDeviceId']));
                setGgdOpenModal(true);
              }}
            >
              编辑
            </Button>
          );
        },
      },
      {
        title: '关联热区',
        dataIndex: 'url',
        render: (_: number, record: AreaMapProp, index: number) => {
          const rowIndex = (fullDataSource ?? []).findIndex(r => r.id === record.id);
          return (
            <Button
              disabled={!isEditing}
              type="link"
              onClick={() => {
                setCurrentIndex(rowIndex);
                hotZoneForm.setFieldsValue({
                  url: form?.getFieldValue(['areaMapList', rowIndex, 'url']),
                  newPage: form?.getFieldValue(['areaMapList', rowIndex, 'newPage']) ?? true,
                });
                setHotZoneOpenModal(true);
              }}
            >
              编辑
            </Button>
          );
        },
      },
    ];

    return (
      <Row gutter={8}>
        <Col span={24}>
          <Table
            columns={areaMapColumns}
            rowKey="id"
            dataSource={pagedData}
            scroll={{ x: 'max-content' }}
            pagination={false}
          />
          <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
        </Col>
        {/* 照明设备 */}
        {openModal && (
          <Modal
            title="设备选择"
            open={openModal}
            destroyOnClose
            wrapClassName={styles.customModal}
            size="big"
            onOk={handleOK}
            onCancel={() => {
              setOpenModal(false);
              setCurrentIndex(undefined);
            }}
          >
            <DeviceModal ref={deviceRef} bindDeviceIds={bindDeviceIds} />
          </Modal>
        )}
        {/* 高杆灯 */}
        {ggdOpenModal && (
          <Modal
            title="设备选择"
            open={ggdOpenModal}
            destroyOnClose
            wrapClassName={styles.customModal}
            size="big"
            onOk={handleGgdOK}
            onCancel={() => {
              setGgdOpenModal(false);
              setCurrentIndex(undefined);
            }}
          >
            <DeviceGgdModal ref={deviceGgdRef} bindGgdDeviceIds={bindGgdDeviceIds} />
          </Modal>
        )}
        {/* 太阳能路灯 */}
        {sunLightOpenModal && (
          <Modal
            title="设备选择"
            open={sunLightOpenModal}
            destroyOnClose
            wrapClassName={styles.customModal}
            size="big"
            onOk={handleSunLightOK}
            onCancel={() => {
              setSunLightOpenModal(false);
              setCurrentIndex(undefined);
            }}
          >
            <DeviceSunLightModal ref={deviceSunLightRef} bindSunLightDeviceIds={bindSunLightDeviceIds} />
          </Modal>
        )}
        {/* 关联热区 */}
        {hotZoneOpenModal && (
          <Modal
            title="编辑"
            open={hotZoneOpenModal}
            destroyOnClose
            onOk={handleHotZoneOK}
            onCancel={() => {
              setHotZoneOpenModal(false);
              setCurrentIndex(undefined);
              hotZoneForm.resetFields();
            }}
          >
            <Form form={hotZoneForm} layout="horizontal">
              <Form.Item name="url" label="点击热区跳转页面">
                <Input placeholder="请输入跳转页面地址" />
              </Form.Item>
              <Form.Item name="newPage" label="是否新窗口打开" initialValue={true}>
                <Radio.Group>
                  <Radio value={true}>是</Radio>
                  <Radio value={false}>否</Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
          </Modal>
        )}
        {/* 绑定用能单元（多选） */}
        {bindEnergyUnitModalOpen && (
          <BindEnergyUnitModal
            open={bindEnergyUnitModalOpen}
            setOpen={setBindEnergyUnitModalOpen}
            energyUnitIds={form?.getFieldValue(['areaMapList', currentIndex!, 'overviewEnergyUnitIds'])}
            energyGroupId={form?.getFieldValue(['areaMapList', currentIndex!, 'overviewEnergyUnitGroupId'])}
            onOk={({ energyUnitIds, energyGroupId }) => {
              const areaMapItem = form!.getFieldValue('areaMapList');
              form?.setFieldsValue({
                areaMapList: set(areaMapItem, [currentIndex!, 'overviewEnergyUnitIds'], energyUnitIds),
              });
              form?.setFieldsValue({
                areaMapList: set(areaMapItem, [currentIndex!, 'overviewEnergyUnitGroupId'], energyGroupId),
              });
            }}
          />
        )}
      </Row>
    );
  }
);

export default AreaMapTable;
